import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { env } from '@/env/client';
import { Capacitor } from '@capacitor/core';
import { convexQuery } from '@convex-dev/react-query';
import type { CustomerInfo as CustomerInfoNative } from '@revenuecat/purchases-capacitor';
import type { CustomerInfo as CustomerInfoWeb } from '@revenuecat/purchases-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '~/convex/_generated/api';
import { RevenuecatMobileProvider } from './mobile';
import type { UnifiedOfferings } from './types';
import { RevenuecatWebProvider } from './web';

const PRO_SUBSCRIPTION_NAME = 'VoiceFill Medical Pro';

const hasActiveSubscription = (
  customerInfo: CustomerInfoWeb | CustomerInfoNative | null | undefined,
): boolean => {
  return customerInfo?.entitlements.active[PRO_SUBSCRIPTION_NAME] !== undefined;
};

interface ContextState {
  customerInfo: CustomerInfoWeb | CustomerInfoNative | null;
  offerings: UnifiedOfferings | null;
  showPaywall: (onSuccess?: () => void) => Promise<void>;
  isSubscribed: boolean;
  isLoading: boolean;
  restorePurchases: () => Promise<void>;
  manageSubscriptions: () => Promise<void>;
}

const getRCAPIKey = () => {
  const platform = Capacitor.getPlatform();

  if (env.VITE_RC_TEST_API_KEY) {
    return env.VITE_RC_TEST_API_KEY;
  }

  if (platform === 'ios') {
    return env.VITE_RC_PUBLIC_IOS_API_KEY;
  }

  if (platform === 'android') {
    return env.VITE_RC_PUBLIC_GOOGLE_API_KEY;
  }

  if (platform === 'web') {
    return env.VITE_RC_PUBLIC_WEB_API_KEY;
  }

  return null;
};

const RevenueCatContext = createContext<ContextState | null>(null);

const platform = Capacitor.getPlatform();
const isMobile = platform === 'ios' || platform === 'android';
const adapter = isMobile ? RevenuecatMobileProvider : RevenuecatWebProvider;

export const RevenuecatProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [isPaywallLoading, setIsPaywallLoading] = useState(false);

  const { data: user } = useQuery(convexQuery(api.user.currentUser, {}));

  useEffect(() => {
    const init = async () => {
      if (!user) return;

      try {
        const apiKey = getRCAPIKey();
        if (!apiKey) throw new Error('RevenueCat API key is not set');

        await adapter.init({ apiKey, appUserId: user._id });
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize RevenueCat:', error);
      }
    };

    init();
  }, [user]);

  const { data: customerInfo, isLoading: isCustomerInfoLoading } = useQuery({
    queryKey: ['revenuecat', 'customerInfo', user?._id],
    queryFn: async () => {
      return adapter.getCustomerInfo();
    },
    enabled: isInitialized && !!user,
  });

  const { data: offerings, isLoading: isOfferingsLoading } = useQuery({
    queryKey: ['revenuecat', 'offerings'],
    queryFn: async () => {
      return adapter.getOfferings();
    },
    enabled: isInitialized,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const isSubscribed = hasActiveSubscription(customerInfo);

  const isLoading =
    !isInitialized ||
    isCustomerInfoLoading ||
    isOfferingsLoading ||
    isPaywallLoading;

  const showPaywall = useCallback(
    async (onSuccess?: () => void) => {
      setIsPaywallLoading(true);
      setIsPaywallOpen(true);

      setTimeout(async () => {
        try {
          await adapter.presentPaywall();
          await queryClient.invalidateQueries({
            queryKey: ['revenuecat', 'customerInfo'],
          });

          const info = await adapter.getCustomerInfo();
          const isNowSubscribed = hasActiveSubscription(info);

          if (isNowSubscribed) {
            onSuccess?.();
          }

          setIsPaywallOpen(false);
        } catch (error) {
          console.error(error);
          setIsPaywallOpen(false);
        } finally {
          setIsPaywallLoading(false);
        }
      }, 100);
    },
    [queryClient],
  );

  const restorePurchases = useCallback(async () => {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    setIsPaywallLoading(true);

    try {
      await adapter.restorePurchases();
      await queryClient.invalidateQueries({
        queryKey: ['revenuecat', 'customerInfo'],
      });

      const info = await adapter.getCustomerInfo();
      const hasSubscription = hasActiveSubscription(info);

      if (hasSubscription) {
        toast.success(t('settings:subscription.purchasesRestoredSuccessfully'));
      } else {
        toast.error(t('settings:subscription.noPurchasesFound'));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsPaywallLoading(false);
    }
  }, [t, queryClient]);

  const handleOpenPaywallDialogChange = useCallback((open: boolean) => {
    setIsPaywallOpen(open);
    if (!open) {
      setIsPaywallLoading(false);
    }
  }, []);

  const manageSubscriptions = useCallback(async () => {
    try {
      await adapter.manageSubscriptions();
    } catch (error) {
      console.error(error);
      toast.error(t('settings:subscription.manageSubscriptionError'));
    }
  }, [t]);

  return (
    <RevenueCatContext.Provider
      value={{
        customerInfo: customerInfo ?? null,
        offerings: offerings ?? null,
        isSubscribed,
        isLoading,
        showPaywall,
        restorePurchases,
        manageSubscriptions,
      }}>
      {children}
      {Capacitor.getPlatform() === 'web' && (
        <Dialog
          open={isPaywallOpen}
          onOpenChange={handleOpenPaywallDialogChange}
          modal={false}>
          <DialogContent id="paywall-dialog" forceOverlay>
            <DialogHeader>
              <div className="flex min-h-[200px] items-center justify-center">
                <Loader2 className="size-10 animate-spin" />
              </div>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      )}
    </RevenueCatContext.Provider>
  );
};

export const useRevenuecat = () => {
  const context = useContext(RevenueCatContext);
  if (!context) {
    throw new Error('useRevenuecat must be used within a RevenuecatProvider');
  }
  return context;
};
